import { useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GridSortModel } from "@mui/x-data-grid";
import { GridPaginationModel } from "@mui/x-data-grid";

interface QueryParams {
  page: number;
  pageSize: number;
  searchText: string;
  sortField: string | null;
  sortOrder: "asc" | "desc" | null;
}

interface UseQueryParamsReturn extends QueryParams {
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
  setQueryParams: (params: Partial<QueryParams>) => void;
  handleTableChange: (
    gridPaginationModel: GridPaginationModel,
    gridSortModel: GridSortModel
  ) => void;
}

/**
 * A hook to manage pagination, sorting, and search parameters via URL query params
 * @param defaultPageSize Default page size value (defaults to 10)
 * @returns Query parameters and derived values for MUI DataGrid
 */
export const useQueryParams = (defaultPageSize = 100): UseQueryParamsReturn => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Parse a numeric query parameter with validation
   * @param paramName Name of the parameter in URL
   * @param defaultValue Default value to use if parameter is invalid or missing
   * @param minValue Minimum allowed value (optional)
   */
  const parseNumericParam = useCallback(
    (paramName: string, defaultValue: number, minValue?: number): number => {
      const param = searchParams.get(paramName);
      if (!param) return defaultValue;

      const parsedValue = parseInt(param, 10);

      // Check if parsed value is valid
      if (isNaN(parsedValue)) return defaultValue;

      // Apply minimum value constraint if provided
      if (minValue !== undefined && parsedValue < minValue) {
        return defaultValue;
      }

      return parsedValue;
    },
    [searchParams]
  );

  /**
   * Parse an enum-like query parameter with validation
   * @param paramName Name of the parameter in URL
   * @param allowedValues Array of allowed values
   * @param defaultValue Default value to use if parameter is invalid or missing
   */
  const parseEnumParam = useCallback(
    <T>(
      paramName: string,
      allowedValues: T[],
      defaultValue: T | null
    ): T | null => {
      const param = searchParams.get(paramName);
      if (!param) return defaultValue;

      return allowedValues.includes(param as unknown as T)
        ? (param as unknown as T)
        : defaultValue;
    },
    [searchParams]
  );

  const page = parseNumericParam("page", 1, 1);
  const pageSize = parseNumericParam("pageSize", defaultPageSize, 1);
  const searchText = searchParams.get("searchText") || "";
  const sortField = searchParams.get("sortField");
  const sortOrder = parseEnumParam<"asc" | "desc">(
    "sortOrder",
    ["asc", "desc"],
    null
  );

  const sortModel: GridSortModel = useMemo(
    () => (sortField ? [{ field: sortField, sort: sortOrder || "asc" }] : []),
    [sortField, sortOrder]
  );

  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  /**
   * Updates URL query parameters and navigates to the new URL
   *
   * @param params - Object containing query parameters to update
   * @example
   * // Set page number
   * setQueryParams({ page: 2 });
   *
   * @example
   * // Change sort order
   * setQueryParams({ sortField: 'name', sortOrder: 'asc' });
   *
   * @example
   * // Apply search filter and reset to first page
   * setQueryParams({ searchText: 'example query', page: 1 });
   *
   * @example
   * // Clear sorting
   * setQueryParams({ sortField: null, sortOrder: null });
   *
   * @example
   * // Update multiple parameters at once
   * setQueryParams({
   *   page: 1,
   *   pageSize: 25,
   *   searchText: 'search term'
   * });
   */
  const setQueryParams = useCallback(
    (params: Partial<QueryParams>) => {
      const newParams = new URLSearchParams(searchParams.toString());

      if (params.page !== undefined) {
        const safePageValue = params.page === 0 ? 1 : params.page;
        newParams.set("page", String(safePageValue));
      }

      if (params.pageSize !== undefined) {
        newParams.set("pageSize", String(params.pageSize));
      }

      // Handle search text
      if (params.searchText !== undefined) {
        if (params.searchText) {
          newParams.set("searchText", params.searchText);
        } else {
          newParams.delete("searchText");
        }
      }

      // Handle sort field
      if (params.sortField !== undefined) {
        if (!params.sortField) {
          newParams.delete("sortField");
        } else {
          newParams.set("sortField", params.sortField);
        }
      }

      // Handle sort order
      if (params.sortOrder !== undefined) {
        if (params.sortOrder === null) {
          newParams.delete("sortOrder");
        } else {
          newParams.set("sortOrder", params.sortOrder);
        }
      }

      navigate(`?${newParams.toString()}`, { replace: true });
    },
    [searchParams, navigate]
  );

  /**
   * Handles table changes from MUI DataGrid and updates URL query parameters
   *
   * @param gridPaginationModel - Pagination model from MUI DataGrid
   * @param gridSortModel - Sort model from MUI DataGrid
   */
  const handleTableChange = useCallback(
    (
      gridPaginationModel: GridPaginationModel,
      gridSortModel: GridSortModel
    ) => {
      const params: Partial<QueryParams> = {
        page: gridPaginationModel.page,
        pageSize: gridPaginationModel.pageSize,
      };

      // If sort model has data, add sort params
      if (gridSortModel.length > 0) {
        params.sortField = gridSortModel[0].field;
        params.sortOrder = gridSortModel[0].sort as "desc" | "asc";
      } else {
        // Explicitly setting null will remove these parameters from URL
        params.sortField = null;
        params.sortOrder = null;
      }

      setQueryParams(params);
    },
    [setQueryParams]
  );

  return useMemo(
    () => ({
      page,
      pageSize,
      searchText,
      sortField,
      sortOrder,
      sortModel,
      paginationModel,
      setQueryParams,
      handleTableChange,
    }),
    [
      page,
      pageSize,
      searchText,
      sortField,
      sortOrder,
      sortModel,
      paginationModel,
      setQueryParams,
      handleTableChange,
    ]
  );
};
